
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { VAT_INCLUSION_FACTOR, VAT_EXCLUSION_FACTOR } from '../constants/index';

export default {
    props: {
        restaurant: { type: Object },
    },
    data: () => ({
        loading: false,
    }),
    computed: {
        ...mapGetters('order', [
            'address',
            'items',
            'totalPrice',
            'originalPrice',
            'restaurantLocation',
            'deliveryMethod',
            'deliveryOptionId',
            'delivery',
            'bagFee',
            'serviceFee',
            'coupon',
            'tempName',
            'groupCartSlave',
            'groupCartUUID',
        ]),
        ...mapGetters(['loggedInUser', 'vat']),
        ...mapGetters('order', ['isDelivery', 'address']),
        ...mapGetters('supplier', ['isWhitelabel']),
        addressStreet() {
            return this.address?.address;
        },
        addressZipCity() {
            return `${this.address?.postal} ${this.address?.city}`;
        },
        distanceStr() {
            return `${this.restaurantLocation?.distance?.toString().replace('.', ',')} km`;
        },
        routeName() {
            return this.$route.name;
        },
        isCheckout() {
            return this.routeName === 'checkout' || this.routeName === 'checkout-new';
        },
        isDelivery() {
            return this.deliveryMethod === 'delivery';
        },
        deliveryPrice() {
            if (this.isDelivery) {
                if (this.vat === 'excl') {
                    return this.delivery && this.delivery.price ? this.delivery.price * VAT_EXCLUSION_FACTOR : 0;
                } else {
                    return (this.delivery && this.delivery.price) || 0;
                }
            } else {
                return null;
            }
        },
        restaurantName() {
            if (this.restaurantLocation) {
                return `${this.restaurantLocation.restaurant_name} - ${this.restaurantLocation.name}`;
            }
            return null;
        },
        locations() {
            return this.restaurant?.locations;
        },
        isOpen() {
            return this.restaurantLocation?.is_open || false;
        },
        kitchenIsOpen() {
            return this.restaurantLocation?.kitchen_is_open || false;
        },
        tempClosed() {
            return this.restaurantLocation?.temp_closed || false;
        },
        disabled() {
            if (!this.restaurantLocation) return true;

            if (
                (this.isDelivery && !this.restaurantLocation.delivery_option) ||
                (this.isDelivery &&
                    this.restaurantLocation.delivery_option &&
                    !this.restaurantLocation.delivery_option.inside_radius)
            )
                return true;
            if (!this.isDelivery && !this.restaurantLocation.pickup_option) return true;
            if (!this.restaurantLocation.kitchen_is_open && this.restaurantLocation.preorder_enabled) {
                return false;
            }
            if (!this.kitchenIsOpen || !this.isOpen || this.tempClosed) return true;
            return false;
        },
        btnText() {
            console.log('Kitchen is open', this.restaurantLocation?.kitchen_is_open);
            console.log('Preorder enabled', this.restaurantLocation?.preorder_enabled);
            if (this.groupCartSlave) {
                return 'Tilføj til gruppebestilling';
            }
            if (this.groupCartSlave && !this.groupCartName) {
                return 'Tilføj navn til bestilling';
            }

            // Add null check for restaurantLocation
            if (!this.restaurantLocation) {
                return 'Indlæser...';
            }

            if (
                (this.isDelivery && !this.restaurantLocation.delivery_option) ||
                (this.isDelivery &&
                    this.restaurantLocation.delivery_option &&
                    !this.restaurantLocation.delivery_option.inside_radius)
            )
                return 'Levering ikke muligt';
            if (!this.isDelivery && !this.restaurantLocation.pickup_option) return 'Afhentning ikke muligt';
            if (!this.restaurantLocation.kitchen_is_open && this.restaurantLocation.preorder_enabled) {
                return 'Checkout';
            }
            if (!this.kitchenIsOpen) return 'Køkkenet har lukket';
            if (!this.isOpen) return 'Lukket';
            if (this.tempClosed) return 'Midlertidigt lukket';
            return 'Checkout';
        },
        groupCartName() {
            return (this.loggedInUser && this.loggedInUser.name) || this.tempName;
        },
        updatingOrder() {
            const urlSearchParams = new URLSearchParams(window.location.search);
            const params = Object.fromEntries(urlSearchParams.entries());
            if (params.updateOrder) {
                return params.updateOrder;
            }
            return null;
        },
    },
    watch: {
        items(items) {
            if (!items.length) {
                this.closeSideDrawer();
            }
        },
        coupon() {
            this.checkout();
        },
    },
    methods: {
        ...mapActions('toast', ['setToast']),
        ...mapMutations(['setDesktopCheckoutStarted']),
        ...mapMutations('order', [
            'setCartVerified',
            'setItems',
            'setDelivery',
            'setBagFee',
            'setServiceFee',
            'removeCoupon',
            'clearCart',
        ]),
        ...mapMutations('sideDrawer', ['setSideDrawer', 'closeSideDrawer']),
        ...mapMutations('modal', ['setModal']),
        async checkout() {
            // Allow guests to proceed to checkout. If the user is not logged in we will still
            // continue with cart validation and, once finished, redirect them to the
            // checkout page and open the Create / Login modal so account creation happens
            // directly inside the checkout flow instead of being a separate blocking step.

            // Track checkout initiated in PostHog
            if (window.posthog) {
                window.posthog.capture('checkout_initiated', {
                    total_price: this.totalPrice,
                    total_items: this.items.length,
                    delivery_method: this.deliveryMethod,
                    restaurant_name: this.restaurantName,
                });
            }

            // Check for valid delivery address
            if (this.isDelivery && !this.address) {
                if (this.loggedInUser && this.loggedInUser.addresses && this.loggedInUser.addresses.length) {
                    this.setModal({ type: 'AddressPick' });
                    return;
                } else {
                    this.setModal({ type: 'AddressCheck' });
                }
                return;
            }
            if (this.loading || (!this.isOpen && !this.restaurantLocation?.preorder_enabled)) return false;
            if (this.groupCartSlave) {
                if (!this.groupCartName) {
                    this.setModal({ type: 'GroupCartSetName' });
                    return;
                }
                await this.addToGroupCart();
                return;
            }
            try {
                this.loading = true;

                // Ensure we have a deliveryOptionId before validation
                try {
                    await this.$store.dispatch('order/ensureDeliveryOptionId');
                } catch (error) {
                    this.setToast({ msg: 'Leveringsmetode ikke tilgængelig. Prøv igen senere.', duration: 3 });
                    this.loading = false;
                    return;
                }

                if (this.coupon && this.coupon.min_cart_value > this.originalPrice) {
                    await this.removeCoupon();
                }
                const cartPayload = {
                    cart: this.items,
                };
                if (this.coupon) {
                    cartPayload.coupon = this.coupon;
                }
                const payload = {
                    cart: JSON.stringify(cartPayload),
                    restaurant_location_id: this.restaurantLocation?.id,
                    delivery_option_id: this.deliveryOptionId,
                };
                // If the user has selected a preorder time (or forced-preorder requires it), always include it in the validation payload
                const preorderTime = this.$store.getters['order/preorderTime'];
                if (preorderTime) {
                    payload.preorder_time = preorderTime;
                }
                if (this.isDelivery) {
                    if (!this.address) {
                        this.setToast({ msg: 'Du skal vælge en leveringsadresse for at fortsætte' });
                        return;
                    }
                    payload.address = JSON.stringify(this.address);
                }
                const {
                    // eslint-disable-next-line camelcase
                    data: { cart, delivery, bag_fee, service_fee },
                } = await this.$axios.post('/validate-cart', payload);
                const cartTotalValid = this.checkCartTotal({
                    local: this.vat === 'excl' ? this.totalPrice * VAT_INCLUSION_FACTOR : this.totalPrice,
                    server: cart.cart_total,
                });
                const discountValid =
                    !this.coupon ||
                    this.checkCartTotal({
                        local:
                            (Math.round((this.originalPrice - this.totalPrice) * 100) / 100) *
                            (this.vat === 'excl' ? VAT_INCLUSION_FACTOR : 1),
                        server: Math.round(cart.discount * 100) / 100,
                    });
                if (!cartTotalValid) {
                    const cartTotalInvalidBugsnagError = {
                        totalPrice: this.totalPrice,
                        server: cart.cart_total,
                        cartPayload,
                        payload,
                    };
                    this.$bugsnag.notify(new Error('cart total is invalid'), function (event) {
                        event.addMetadata('custom', cartTotalInvalidBugsnagError);
                    });
                    this.loading = false;
                }
                if (!discountValid) {
                    const discountInvalidBugsnagError = {
                        totalPrice: this.totalPrice,
                        server: cart.cart_total,
                        originalPrice: this.originalPrice - this.totalPrice,
                        cartDiscount: cart.discount,
                        cartPayload,
                        payload,
                    };
                    this.$bugsnag.notify(new Error('discount is invalid'), function (event) {
                        event.addMetadata('custom', discountInvalidBugsnagError);
                    });
                    this.loading = false;
                }
                if (cartTotalValid && discountValid) {
                    await this.setCartVerified(true);
                    await this.setItems(cart.cart);
                    await this.setDelivery(delivery);
                    await this.setBagFee(bag_fee);
                    await this.setServiceFee(service_fee);
                    this.loading = false;
                    setTimeout(() => {
                        // Ecommerce tracking - START
                        const cartItems = [];
                        this.items.forEach(item => {
                            cartItems.push({
                                name: item.name,
                                id: item.id,
                                price: item.price,
                                brand: this.restaurantName,
                                quantity: item.quantity,
                            });
                        });
                        if (typeof window.dataLayer !== 'undefined') {
                            window.dataLayer.push({ ecommerce: null });
                            window.dataLayer.push({
                                event: 'checkout',
                                ecommerce: {
                                    checkout: {
                                        actionField: {
                                            step: 1,
                                        },
                                        products: cartItems,
                                    },
                                },
                            });
                        }
                        // Ecommerce tracking - END
                        if (this.loggedInUser) {
                            const path = window.location.pathname;
                            const queryString = window.location.search;
                            const pathWithParams = `${path}${queryString}`;
                            if (!pathWithParams.includes('backRedirectTo')) {
                                this.$nextTick(
                                    this.$router.push('/checkout-redirect?backRedirectTo=' + pathWithParams)
                                );
                            } else {
                                this.$nextTick(this.$router.push('/checkout-redirect'));
                            }
                        } else {
                            // Redirect the guest user to the checkout page and open the
                            // Create / Login modal so they can create an account without
                            // leaving the checkout context.
                            const path = window.location.pathname;
                            const queryString = window.location.search;
                            const pathWithParams = `${path}${queryString}`;
                            if (!pathWithParams.includes('backRedirectTo')) {
                                this.$nextTick(
                                    this.$router.push('/checkout-redirect?backRedirectTo=' + pathWithParams)
                                );
                            } else {
                                this.$nextTick(this.$router.push('/checkout-redirect'));
                            }

                            // this.setModal({ type: 'CreateOrLogin' });
                        }
                    }, 300);
                } else {
                    this.setToast({});
                }
            } catch (error) {
                this.$bugsnag.notify(new Error(error));
                this.loading = false;
                if (error.response && error.response.data) {
                    const { data } = error.response;
                    if (data.errorMessage) {
                        this.setToast({ msg: data.errorMessage, duration: 3 });
                    }
                } else {
                    this.setToast({});
                }
            }
        },
        checkCartTotal({ local, server }) {
            return local - server > -1 && local - server < 1;
            // return local === server;
        },
        async addToGroupCart() {
            const cartPayload = {
                cart: this.items,
            };
            const payload = {
                cart: JSON.stringify(cartPayload),
                name: this.groupCartName,
                uuid: this.groupCartUUID,
            };
            try {
                await this.$axios.post('/group-cart/add', payload);
                this.setToast({ msg: 'Tilføjet til gruppebestillingen. Du kan nu lukke vinduet', duration: 5 });
                this.clearCart();
            } catch (error) {
                this.$bugsnag.notify(new Error(error));
            }
        },
        closeCart() {
            this.closeSideDrawer();
        },
        setGroupCartModal() {
            if (!this.loggedInUser || this.groupCartSlave) {
                // eslint-disable-next-line no-useless-return
                return;
            } else {
                this.setModal({ type: 'GroupCart' });
            }
        },
        async updateOrder() {
            const cartJson = JSON.stringify({
                cart: this.items,
            });
            try {
                this.loading = true;
                const body = {
                    cart_json: cartJson,
                    coupon: 'BETECH10',
                };
                // eslint-disable-next-line no-unused-vars
                const { data } = await this.$axios.post(`/update-order/${this.updatingOrder}`, body);
                this.clearCart();
                this.setToast({
                    msg: 'Ordren er nu opdateret. Vi tager dig til din leveringsoversigt nu.',
                    duration: 5,
                });
                await new Promise(resolve => setTimeout(resolve, 3000));
                this.$router.push('/orders');
                this.loading = false;
            } catch (error) {
                this.$bugsnag.notify(new Error(error));
                console.log({ error });
                this.loading = false;
                this.setToast({
                    msg: 'Der skete en fejl ved opdatering af ordren. Prøv igen eller kontakt vores support.',
                    duration: 5,
                });
            }
        },
    },
};
